import { Link, useParams, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, articles } from '@/mocks/articles';
import ArticleSchema from '@/components/base/ArticleSchema';
import BreadcrumbListSchema from '@/components/base/BreadcrumbListSchema';
import WebPageSchema from '@/components/base/WebPageSchema';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [slug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const relatedArticles = articles
    .filter(a => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ArticleSchema
        headline={article.title}
        description={article.excerpt}
        image={article.imageUrl}
        datePublished="2026-07-07"
        dateModified="2026-07-07"
        authorName={article.author}
        url={`http://cg91.cn/article/${article.slug}`}
        category={article.category}
      />
      <WebPageSchema
        url={`http://cg91.cn/article/${article.slug}`}
        name={`${article.title} — The Vertex`}
        description={article.excerpt}
        datePublished="2026-07-07"
        dateModified="2026-07-07"
      />
      <BreadcrumbListSchema
        items={[
          { name: 'Home', url: 'http://cg91.cn/' },
          { name: article.category.charAt(0).toUpperCase() + article.category.slice(1), url: `http://cg91.cn/category/${article.category}` },
          { name: article.title, url: `http://cg91.cn/article/${article.slug}` },
        ]}
      />
      <main className="min-h-screen bg-background-50 font-body">
        <Navbar />

        <article className="pt-20 md:pt-24 pb-14 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors cursor-pointer capitalize">{article.category}</Link>
              <i className="ri-arrow-right-s-line text-foreground-300"></i>
              <span className="text-foreground-600 line-clamp-1">{article.title}</span>
            </nav>

            <div className="mb-6 md:mb-8">
              <Link
                to={`/category/${article.category}`}
                className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium tracking-wide uppercase mb-4 cursor-pointer hover:bg-accent-200 transition-colors"
              >
                {article.category}
              </Link>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 md:mb-5">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="flex items-center gap-3 mb-8 md:mb-10 pb-6 border-b border-background-200">
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-secondary-100 flex items-center justify-center text-sm font-bold text-secondary-700">
                {article.authorAvatar}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800">{article.author}</p>
                <div className="flex items-center gap-2 text-xs text-foreground-400">
                  <span>{article.date}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </div>

            <div className="mb-8 md:mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-auto max-h-[500px] object-cover object-top"
                title={`${article.title} - The Vertex`}
              />
            </div>

            <div className="prose-custom">
              <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-5">
                <p className="text-lg md:text-xl font-heading text-foreground-900 leading-relaxed">
                  In a world increasingly shaped by rapid technological advancement and shifting cultural paradigms, the story of {article.category} continues to unfold in ways that challenge our fundamental assumptions about progress and human potential.
                </p>

                <p>
                  The landscape has transformed dramatically over the past eighteen months, with breakthroughs arriving at a pace that even the most optimistic forecasters failed to predict. Industry insiders who once spoke cautiously about five-year timelines are now revising their projections down to eighteen months or less — a shift that speaks volumes about the accelerating nature of innovation in this space.
                </p>

                <p>
                  When researchers at the forefront of this field began publishing their findings in early 2026, the reaction across both academic and commercial sectors was immediate and profound. Venture capital firms that had been sitting on the sidelines suddenly found themselves scrambling to understand the implications, while established players rushed to announce their own initiatives in response.
                </p>

                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 pt-4">
                  The Numbers Tell a Compelling Story
                </h2>

                <p>
                  According to the latest industry reports, investment in this sector has surged by 340% compared to the same period last year. More than 1,200 new startups have emerged globally, collectively raising over $48 billion in funding. The ecosystem has matured beyond the early-adopter phase, with mainstream enterprises now accounting for 62% of total spending — up from just 23% two years ago.
                </p>

                <p>
                  &ldquo;What we are witnessing is not merely an incremental improvement,&rdquo; explains Dr. Elena Vasquez, director of the Institute for Applied Innovation. &ldquo;This represents a fundamental paradigm shift in how we approach problem-solving across multiple disciplines. The convergence of advances in materials science, algorithmic efficiency, and distributed computing has created a perfect storm of possibility.&rdquo;
                </p>

                <blockquote className="border-l-2 border-primary-300 pl-5 py-1 my-6">
                  <p className="font-heading text-lg italic text-foreground-600">
                    &ldquo;The question is no longer whether this transformation will happen, but how thoughtfully we can guide its trajectory to benefit the broadest possible cross-section of society.&rdquo;
                  </p>
                  <cite className="text-xs text-foreground-400 not-italic mt-2 block">— Dr. Elena Vasquez, Director of the Institute for Applied Innovation</cite>
                </blockquote>

                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 pt-4">
                  Challenges on the Horizon
                </h2>

                <p>
                  Despite the overwhelming optimism, significant challenges remain. Regulatory frameworks across major markets are still evolving, creating uncertainty for companies attempting to plan multi-year strategies. The European Union has moved faster than most, proposing a comprehensive regulatory package that could serve as a template for other jurisdictions — though critics argue that its prescriptive approach may stifle innovation.
                </p>

                <p>
                  Meanwhile, questions about ethical implementation continue to surface in academic and public discourse. A recent survey of 5,000 industry professionals found that 71% believe current governance structures are inadequate to address the complex challenges posed by these emerging technologies. The conversation has shifted from whether regulation is needed to what form it should take, and how it can be designed to protect public interests without hampering beneficial progress.
                </p>

                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 pt-4">
                  Looking Ahead
                </h2>

                <p>
                  As we move through the second half of 2026, the trajectory appears clear: this sector will continue to drive not only technological change but also broader societal transformation. The companies and countries that position themselves effectively today will define the competitive landscape for the decade ahead. The conversations happening now — in boardrooms, legislative chambers, and academic conferences — will echo for generations.
                </p>

                <p>
                  For readers looking to stay informed, the coming months promise to be extraordinarily eventful. Major announcements are expected from several key players before the end of the third quarter, and the next round of regulatory decisions could fundamentally reshape the competitive dynamics. The only certainty is that the pace of change shows no signs of slowing.
                </p>
              </div>
            </div>

            <div className="mt-10 md:mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xs font-label font-medium text-foreground-500 uppercase tracking-wider">Share:</span>
                {['ri-twitter-x-line', 'ri-linkedin-line', 'ri-facebook-line', 'ri-link'].map((icon) => (
                  <button
                    key={icon}
                    className="w-8 h-8 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
                    aria-label={`Share on ${icon.replace('ri-', '').replace('-line', '')}`}
                  >
                    <i className={`${icon} text-sm`}></i>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 md:mb-10">
                More in <span className="text-primary-600 capitalize">{article.category}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden cursor-pointer hover:border-background-300 transition-colors"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={`${rel.title} - The Vertex`}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-foreground-400">
                        <span>{rel.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{rel.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </main>
    </>
  );
}